import { useCallback, useEffect } from "react";
import { useNavigate, useSearch } from "@organizacaox/lai-design-system/router";

export function searchText(search: Record<string, unknown>, key: string) {
  return typeof search[key] === "string" ? (search[key] as string) : "";
}

export function paginationSearch(search: Record<string, unknown>, prefix = "", defaultSize = 25) {
  const pageKey = prefix ? `${prefix}Page` : "page";
  const sizeKey = prefix ? `${prefix}PageSize` : "pageSize";
  const rawPage = Number(search[pageKey]);
  const rawSize = Number(search[sizeKey]);
  return {
    page: Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1,
    pageSize: [10, 20, 25, 50, 100].includes(rawSize) ? rawSize : defaultSize,
    pageKey,
    sizeKey,
  };
}

export function useListLocation() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const update = useCallback(
    (patch: Record<string, unknown>, replace = false) => {
      void navigate({
        to: ".",
        search: (previous) => ({ ...previous, ...patch }),
        replace,
        resetScroll: false,
      });
    },
    [navigate],
  );
  return { search, update };
}

export function useUrlTablePagination<T>(rows: readonly T[] | undefined, prefix = "") {
  const { search, update } = useListLocation();
  const { page: requestedPage, pageSize, pageKey, sizeKey } = paginationSearch(search, prefix);
  const total = rows?.length ?? 0;
  const page =
    rows === undefined
      ? requestedPage
      : Math.min(requestedPage, Math.max(1, Math.ceil(total / pageSize)));
  useEffect(() => {
    if (rows !== undefined && page !== requestedPage) update({ [pageKey]: page }, true);
  }, [rows, page, requestedPage, pageKey, update]);
  return {
    items: rows?.slice((page - 1) * pageSize, page * pageSize) ?? [],
    pagination: {
      page,
      pageSize,
      total,
      onPageChange: (page: number) => update({ [pageKey]: page }),
      onPageSizeChange: (pageSize: number) => update({ [pageKey]: 1, [sizeKey]: pageSize }),
    },
  };
}

export function useUrlCursorPagination(defaultSize = 25) {
  const { search, update } = useListLocation();
  const { page: requestedPage, pageSize } = paginationSearch(search, "", defaultSize);
  const cursors =
    Array.isArray(search.cursors) &&
    search.cursors.every((value) => typeof value === "string" && value.length <= 500)
      ? (search.cursors as string[])
      : [];
  const page = Math.min(requestedPage, cursors.length + 1);
  return {
    page,
    pageSize,
    cursor: page === 1 ? undefined : cursors[page - 2],
    onPageChange: (nextPage: number, nextCursor?: string | null) => {
      if (nextPage > page) {
        if (nextCursor)
          update({ page: nextPage, cursors: [...cursors.slice(0, page - 1), nextCursor] });
      } else update({ page: nextPage });
    },
    onPageSizeChange: (pageSize: number) => update({ page: 1, pageSize, cursors: undefined }),
  };
}
